import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { Box, Dialog } from '@mui/material'
import InputForm from './InputForm'
import { DialogContent, DialogTitle, DialogActions, Button } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'

const InputModal = ({ open, onClose, title, formData, submitForm = null, cancelFunction, validationSchema }) => {
  const [formDataState, setFormDataState] = useState(formData) // eslint-disable-line no-unused-vars
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '600px',
          backgroundColor: 'itemBackground',
        },
      }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <InputForm
          formId={`${title}-inputForm`}
          inputData={formDataState}
          submitForm={submitForm}
          validationSchema={validationSchema}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={() => cancelFunction()} color='secondary'>
          {submitForm !== null ? 'Cancel' : 'Close'}
        </Button>
        {submitForm !== null ? (
          <Button variant='contained' color='primary' type='submit' form={`${title}-inputForm`}>
            Submit
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

InputModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  formData: PropTypes.array.isRequired,
  submitForm: PropTypes.func.isRequired,
  cancelFunction: PropTypes.func.isRequired,
  validationSchema: PropTypes.object,
}

function normOptionLabel(o) {
  if (!o) return ''
  return o.label ?? o.id ?? o.text ?? String(o ?? '')
}

function normOptionValue(o) {
  if (!o) return ''
  return o.value ?? o.id ?? o.key ?? normOptionLabel(o)
}

function normalizePickList(pickListValues) {
  if (!Array.isArray(pickListValues)) return []
  return pickListValues.map((o) => ({
    label: normOptionLabel(o),
    value: normOptionValue(o),
    raw: o,
  }))
}

function normalizeInitialValue(v) {
  if (Array.isArray(v)) return v
  if (v == null || v === '') return []
  return [v]
}

export function InputModalGrid({
  open,
  onClose,
  title,
  formData,
  submitForm = null,
  cancelFunction,
  disableSubmitUntilChanged = false,
}) {
  const gridRef = useRef(null)
  const { getAgGridTheme } = useInnovaTheme()
  const [selectedRowState, setSelectedRowState] = useState('rigs') 

  const initialRows = useMemo(() => {
    return (formData ?? []).map((row) => {
      const options = normalizePickList(row.pickListValues)
      return {
        tag: row.tag,
        text: row.text ?? row.tag,
        inputType: row.inputType ?? 'picklist',
        options,
        value: normalizeInitialValue(row.value).map((x) => (typeof x === 'object' ? normOptionValue(x) : x)),
      }
    })
  }, [formData])

  const [rowData, setRowData] = useState(initialRows)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setRowData(initialRows)
    setDirty(false)
  }, [initialRows, open])

  const hasSubmit = typeof submitForm === 'function'

  const onCellValueChanged = useCallback(() => {
    setDirty(true)
  }, [])

  const gridSx = useMemo(
    () => ({
      height: 450,
      width: '100%',
      '& .ag-header': { display: 'none' },
      '& .ag-cell': { display: 'flex', alignItems: 'center' },
      '& .ag-row': { minHeight: 44 },
    }),
    [],
  )

  const colDefs = useMemo(() => {
    return [
      {
        headerName: 'Field',
        field: 'text',
        editable: false,
        flex: 1,
        minWidth: 200,
        cellStyle: { fontWeight: 700 },
      },
      {
        headerName: 'Value',
        field: 'value',
        flex: 2,
        minWidth: 320,
        editable: (p) => p.data?.inputType === 'picklist',
        valueFormatter: (p) => {
          const v = p.value
          if (!Array.isArray(v) || v.length === 0) return '—'
          const opts = p.data?.options ?? []
          const map = new Map(opts.map((o) => [o.value, o.label || o.value]))
          return v.map((x) => map.get(x) ?? String(x)).join(', ')
        },
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: (p) => {
          const opts = p.data?.options ?? []
          const values = opts.map((o) => o.value)

          return {
            values,
            multiSelect: true,
            searchType: 'matchAny',
            formatValue: (val) => {
              const found = opts.find((o) => o.value === val)
              return found?.label ?? String(val ?? '')
            },
            valueListMaxHeight: 260,
          }
        },
      },
    ]
  }, [])

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      wrapText: true,
      autoHeight: true,
    }),
    [],
  )

  const getPayload = useCallback(() => {
    const payload = {}
    rowData.forEach((r) => {
      payload[r.tag] = Array.isArray(r.value) ? r.value : []
    })
    return payload
  }, [rowData])

  const handleSubmit = useCallback(() => {
    if (!hasSubmit) return
    submitForm(getPayload())
    onClose?.()
  }, [hasSubmit, submitForm, getPayload, onClose])

  const onRowValueUpdated = useCallback((params) => {
    const updated = params.data
    setRowData((prev) => prev.map((r) => (r.tag === updated.tag ? { ...updated } : r)))
  }, [])

  const onSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api?.getSelectedNodes() ?? []
    if (selectedNodes.length === 0) return
    const selectedData = selectedNodes[0].data
    setSelectedRowState(selectedData.tag)
  }, [])

  const selectAll = useCallback(() => {
    setRowData((prev) =>
      prev.map((r) => {
        if (r.inputType !== 'picklist') return r
        if (r.tag !== selectedRowState) return r
        const all = (r.options ?? []).map((o) => o.value).filter((v) => v != null)
        return { ...r, value: all }
      }),
    )
    
    setDirty(true)
    requestAnimationFrame(() => gridRef.current?.api?.refreshCells({ force: true }))
  }, [selectedRowState])

  const unselectAll = useCallback(() => {
    setRowData((prev) =>
      prev.map((r) => {
        if (r.inputType !== 'picklist') return r
        if (r.tag !== selectedRowState) return r
        return { ...r, value: [] }
      }),
    )

    setDirty(true)
    requestAnimationFrame(() => gridRef.current?.api?.refreshCells({ force: true }))
  }, [selectedRowState])

  const submitDisabled = disableSubmitUntilChanged ? !dirty : false

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 760,
          maxWidth: '92vw',
          backgroundColor: 'itemBackground',
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Box sx={gridSx}>
          <div className={getAgGridTheme()} style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              className='ag-grid-theme-dark'
              ref={gridRef}
              rowData={rowData}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              suppressMovableColumns
              rowSelection='single'
              onCellValueChanged={onCellValueChanged}
              onRowValueUpdated={onRowValueUpdated}
              onSelectionChanged={onSelectionChanged}
              domLayout='normal'
              getRowId={(p) => p.data.tag}
              stopEditingWhenCellsLoseFocus
            />
          </div>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant='outlined' color='secondary' onClick={selectAll}>
            Select all
          </Button>
          <Button variant='outlined' color='secondary' onClick={unselectAll}>
            Unselect all
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant='outlined' onClick={() => cancelFunction?.()} color='secondary'>
            {hasSubmit ? 'Cancel' : 'Close'}
          </Button>

          {hasSubmit ? (
            <Button variant='contained' color='primary' onClick={handleSubmit} disabled={submitDisabled}>
              Submit
            </Button>
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  )
}


InputModalGrid.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  formData: PropTypes.array.isRequired,
  submitForm: PropTypes.func,
  cancelFunction: PropTypes.func.isRequired,
  disableSubmitUntilChanged: PropTypes.bool,
}

export default InputModal
